import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BugIcon, CheckIcon, CopyIcon, DownloadIcon, LinkExternalIcon, ScreenFullIcon, ThumbsdownIcon, ThumbsupIcon, XIcon } from '@primer/octicons-react';
import { LTLoader, LTTooltip } from '@lambdatestincprivate/lt-components';
import { Portal } from '@primer/react';
import { closeIssueDrawer, usePackState } from '../../data/pack-store';
import { openDetailWithUrl } from '../../app/url-sync';
import { CopyLinkButton } from '../CopyLink/CopyLinkButton';
import { actionStepCount } from '../../model/detail';
import { confidenceInfo } from '../../model/issue-type';
import { useArtifactUrl } from '../hooks/useArtifactUrl';
import { StatusIcon } from '../icons/StatusIcon';
import { IssueTypeChip } from './IssueTypeChip';
import { buildTimeline } from './timeline';
import styles from './rca-drawer.module.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var TRAJECTORY_SOON = 'Coming soon — the agent’s full decision trail ✨';
/** Small transient-copied button (header of log block / fix / footer). */
function useCopied() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    copied = _useState2[0],
    setCopied = _useState2[1];
  var timer = useRef(null);
  useEffect(function () {
    return function () {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);
  var copy = function copy(text) {
    if (navigator.clipboard) void navigator.clipboard.writeText(text);
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(function () {
      return setCopied(false);
    }, 1500);
  };
  return [copied, copy];
}

/* Design-provided fold/unfold glyphs for the hidden-steps toggle
 * (Figma 30974-19027), kept verbatim — stroke #6E7781, width 1.25. */
function UnfoldIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("g", {
      clipPath: "url(#ev-rca-unfold-clip)",
      children: /*#__PURE__*/_jsx("path", {
        d: "M2.66732 8.00015H1.33398M6.66732 8.00015H5.33398M10.6673 8.00015H9.33398M14.6673 8.00015H13.334M8.00065 10.6668V14.6668M6.00065 12.6668L8.00065 14.6668L10.0007 12.6668M8.00065 1.3335V5.3335M6.00065 3.3335L8.00065 1.3335L10.0007 3.3335",
        stroke: "#6E7781",
        strokeWidth: "1.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      })
    }), /*#__PURE__*/_jsx("defs", {
      children: /*#__PURE__*/_jsx("clipPath", {
        id: "ev-rca-unfold-clip",
        children: /*#__PURE__*/_jsx("rect", {
          width: "16",
          height: "16",
          fill: "white"
        })
      })
    })]
  });
}
function FoldIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("g", {
      clipPath: "url(#ev-rca-fold-clip)",
      children: /*#__PURE__*/_jsx("path", {
        d: "M8.00065 14.6668V10.6668M6.00065 12.6668L8.00065 10.6668L10.0007 12.6668M8.00065 5.3335V1.3335M6.00065 3.3335L8.00065 5.3335L10.0007 3.3335M2.66732 8.00016H1.33398M6.66732 8.00016H5.33398M10.6673 8.00016H9.33398M14.6673 8.00016H13.334",
        stroke: "#6E7781",
        strokeWidth: "1.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      })
    }), /*#__PURE__*/_jsx("defs", {
      children: /*#__PURE__*/_jsx("clipPath", {
        id: "ev-rca-fold-clip",
        children: /*#__PURE__*/_jsx("rect", {
          width: "16",
          height: "16",
          fill: "white"
        })
      })
    })]
  });
}

/** One timeline row: stepper column (icon + connectors) next to content. */
function TimelineRow(_ref) {
  var icon = _ref.icon,
    first = _ref.first,
    last = _ref.last,
    children = _ref.children;
  return /*#__PURE__*/_jsxs("li", {
    className: styles.stepRow,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.stepper,
      "aria-hidden": "true",
      children: [/*#__PURE__*/_jsx("span", {
        className: first ? styles.connectorTopHidden : styles.connector
      }), /*#__PURE__*/_jsx("span", {
        className: styles.stepIcon,
        children: icon
      }), /*#__PURE__*/_jsx("span", {
        className: last ? styles.connectorHidden : styles.connectorGrow
      })]
    }), /*#__PURE__*/_jsx("span", {
      className: styles.stepBody,
      children: children
    })]
  });
}

/** The Network/Console code block: grey header + numbered mono lines. */
function LogBlock(_ref2) {
  var title = _ref2.title,
    lines = _ref2.lines,
    emptyText = _ref2.emptyText,
    note = _ref2.note;
  var _useCopied = useCopied(),
    _useCopied2 = _slicedToArray(_useCopied, 2),
    copied = _useCopied2[0],
    copy = _useCopied2[1];
  return /*#__PURE__*/_jsxs("div", {
    className: styles.logBlock,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.logBlockHeader,
      children: [/*#__PURE__*/_jsxs("span", {
        className: styles.logBlockTitle,
        children: [title, note && /*#__PURE__*/_jsxs("span", {
          className: styles.logBlockNote,
          children: [" \xB7 ", note]
        })]
      }), lines.length > 0 && /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.logCopyBtn,
        "aria-label": "Copy ".concat(title),
        onClick: function onClick() {
          return copy(lines.map(function (l) {
            return l.text;
          }).join('\n'));
        },
        children: copied ? /*#__PURE__*/_jsx(CheckIcon, {
          size: 16
        }) : /*#__PURE__*/_jsx(CopyIcon, {
          size: 16
        })
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.logBlockBody,
      children: lines.length === 0 ? /*#__PURE__*/_jsx("span", {
        className: styles.logEmpty,
        children: emptyText
      }) : lines.map(function (line, i) {
        return /*#__PURE__*/_jsxs("span", {
          className: styles.logLine,
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.logLineNo,
            children: i + 1
          }), /*#__PURE__*/_jsx("span", {
            className: line.failed ? styles.logLineTextFailed : styles.logLineText,
            children: line.text
          })]
        }, i);
      })
    })]
  });
}

/** Failure screenshot for the Media tab: "Screenshot at failure" card with
 * download + maximize actions (Figma 30947-17310). */
function MediaTab(_ref3) {
  var screenshotPath = _ref3.screenshotPath;
  var _useArtifactUrl = useArtifactUrl(screenshotPath),
    url = _useArtifactUrl.url;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    maximized = _useState4[0],
    setMaximized = _useState4[1];
  var lightboxRef = useRef(null);

  // The lightbox needs focus so its own Escape fires before the drawer's.
  useEffect(function () {
    var _lightboxRef$current;
    if (maximized) (_lightboxRef$current = lightboxRef.current) === null || _lightboxRef$current === void 0 || _lightboxRef$current.focus();
  }, [maximized]);
  var download = function download() {
    var _screenshotPath$split;
    if (!url) return;
    var a = document.createElement('a');
    a.href = url;
    a.download = (_screenshotPath$split = screenshotPath === null || screenshotPath === void 0 ? void 0 : screenshotPath.split('/').pop()) !== null && _screenshotPath$split !== void 0 ? _screenshotPath$split : 'screenshot.png';
    a.click();
  };
  return /*#__PURE__*/_jsxs("div", {
    className: styles.logBlock,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.logBlockHeader,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.logBlockTitle,
        children: "Screenshot at failure"
      }), url && /*#__PURE__*/_jsxs("span", {
        className: styles.mediaActions,
        children: [/*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.logCopyBtn,
          "aria-label": "Download screenshot",
          onClick: download,
          children: /*#__PURE__*/_jsx(DownloadIcon, {
            size: 16
          })
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.logCopyBtn,
          "aria-label": "View screenshot fullscreen",
          onClick: function onClick() {
            return setMaximized(true);
          },
          children: /*#__PURE__*/_jsx(ScreenFullIcon, {
            size: 16
          })
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: url ? styles.mediaBody : styles.logBlockBody,
      children: url ? /*#__PURE__*/_jsx("img", {
        className: styles.mediaShot,
        src: url,
        alt: "Page state at failure"
      }) : /*#__PURE__*/_jsx("span", {
        className: styles.logEmpty,
        children: "No screenshot captured at failure."
      })
    }), maximized && url && /*#__PURE__*/_jsx("button", {
      type: "button",
      className: styles.mediaLightbox,
      "aria-label": "Exit fullscreen screenshot",
      ref: lightboxRef,
      onClick: function onClick() {
        return setMaximized(false);
      },
      onKeyDown: function onKeyDown(e) {
        // Escape ladder: close the lightbox, not the drawer.
        if (e.key === 'Escape') {
          e.stopPropagation();
          setMaximized(false);
        }
      },
      children: /*#__PURE__*/_jsx("img", {
        className: styles.mediaLightboxImg,
        src: url,
        alt: "Page state at failure, fullscreen"
      })
    })]
  });
}
function stepIconFor(role, step) {
  if (role === 'root') return /*#__PURE__*/_jsx(StatusIcon, {
    status: "failed",
    size: 16
  });
  if (role === 'effect') return /*#__PURE__*/_jsx(StatusIcon, {
    status: "broken",
    size: 16
  });
  return /*#__PURE__*/_jsx(StatusIcon, {
    status: step.status === 'unknown' ? 'passed' : step.status,
    size: 16
  });
}

/**
 * The RCA drawer (Figma 30947-17310), fed entirely from the pack's
 * failure record: header chip + confidence, bug banner, Steps-to-Reproduce
 * timeline windowed around the verdict's culprit step, Console/Network/
 * Media slices at failure, suggested fix, and copy actions. No APIs.
 */
export function RcaDrawer() {
  var _record$confidence, _record$title, _record$suggestedFix, _item$category, _record$category;
  var drawer = usePackState(function (s) {
    return s.issueDrawer;
  });
  // Lock the page scroll while the drawer is open (wheel over the
  // backdrop / scroll chaining must not move the content behind).
  useEffect(function () {
    var prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return function () {
      document.body.style.overflow = prev;
    };
  }, []);
  var dialogRef = useRef(null);
  var _useState5 = useState(function () {
      return new Set();
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    expandedSegments = _useState6[0],
    setExpandedSegments = _useState6[1];
  var _useState7 = useState('network'),
    _useState8 = _slicedToArray(_useState7, 2),
    tab = _useState8[0],
    setTab = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    tabsHidden = _useState0[0],
    setTabsHidden = _useState0[1];
  var _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    vote = _useState10[0],
    setVote = _useState10[1];
  var _useCopied3 = useCopied(),
    _useCopied4 = _slicedToArray(_useCopied3, 2),
    fixCopied = _useCopied4[0],
    copyFix = _useCopied4[1];
  var _useCopied5 = useCopied(),
    _useCopied6 = _slicedToArray(_useCopied5, 2),
    rcaCopied = _useCopied6[0],
    copyRca = _useCopied6[1];

  // Focus lands in the dialog on open; restore on close.
  useEffect(function () {
    var _dialogRef$current;
    var before = document.activeElement;
    (_dialogRef$current = dialogRef.current) === null || _dialogRef$current === void 0 || _dialogRef$current.focus();
    return function () {
      var _before$focus;
      return before === null || before === void 0 || (_before$focus = before.focus) === null || _before$focus === void 0 ? void 0 : _before$focus.call(before);
    };
  }, []);
  var drawerData = drawer;
  var timeline = useMemo(function () {
    return drawerData ? buildTimeline(drawerData.steps, drawerData.anchorKey, expandedSegments) : [];
  }, [drawerData, expandedSegments]);
  if (!drawerData) return null;
  var item = drawerData.item,
    record = drawerData.record,
    steps = drawerData.steps,
    anchorKey = drawerData.anchorKey;
  var confidence = confidenceInfo((_record$confidence = record === null || record === void 0 ? void 0 : record.confidence) !== null && _record$confidence !== void 0 ? _record$confidence : item.confidence);
  var title = (_record$title = record === null || record === void 0 ? void 0 : record.title) !== null && _record$title !== void 0 ? _record$title : item.title;
  var loading = drawerData.phase === 'loading';
  var fixText = (_record$suggestedFix = record === null || record === void 0 ? void 0 : record.suggestedFix) !== null && _record$suggestedFix !== void 0 ? _record$suggestedFix : null;
  var fixPrompt = ["Fix this bug: ".concat(title), record !== null && record !== void 0 && record.rootCause ? "Root cause: ".concat(record.rootCause) : null, fixText ? "Suggested fix: ".concat(fixText) : null, record !== null && record !== void 0 && record.pageUrl ? "Page: ".concat(record.pageUrl) : null, record !== null && record !== void 0 && record.message ? "Error: ".concat(record.message) : null].filter(Boolean).join('\n');
  var rcaText = ["RCA: ".concat(title), "Type: ".concat((_item$category = item.category) !== null && _item$category !== void 0 ? _item$category : 'unknown'), confidence ? "Confidence: ".concat(confidence.pct, "% (").concat(confidence.label, ")") : null, record !== null && record !== void 0 && record.rootCause ? "Root cause: ".concat(record.rootCause) : null, fixText ? "How to fix: ".concat(fixText) : null, record !== null && record !== void 0 && record.pageUrl ? "Page: ".concat(record.pageUrl) : null].filter(Boolean).join('\n');
  var onKeyDown = function onKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeIssueDrawer();
    }
  };
  var viewAllSteps = function viewAllSteps() {
    var _ref4, _steps$find$id, _steps$find;
    var anchorId = (_ref4 = (_steps$find$id = (_steps$find = steps.find(function (s) {
      return s.key === anchorKey;
    })) === null || _steps$find === void 0 ? void 0 : _steps$find.id) !== null && _steps$find$id !== void 0 ? _steps$find$id : record === null || record === void 0 ? void 0 : record.step) !== null && _ref4 !== void 0 ? _ref4 : undefined;
    closeIssueDrawer();
    openDetailWithUrl(item.testId, anchorId);
  };
  return (
    /*#__PURE__*/
    // Portaled to the body: inside the TMS mount the drawer must float
    // over the host chrome (navbar, sidebars), not clip inside the
    // viewer's own stacking context.
    _jsx(Portal, {
      children: /*#__PURE__*/_jsx("div", {
        className: styles.overlay,
        onMouseDown: function onMouseDown() {
          return closeIssueDrawer();
        },
        onKeyDown: onKeyDown,
        children: /*#__PURE__*/_jsxs("div", {
          className: styles.drawer,
          role: "dialog",
          "aria-modal": "true",
          "aria-label": "Root cause analysis: ".concat(title),
          tabIndex: -1,
          ref: dialogRef,
          onMouseDown: function onMouseDown(e) {
            return e.stopPropagation();
          },
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.header,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.headerTitle,
              children: "RCA"
            }), /*#__PURE__*/_jsx(IssueTypeChip, {
              category: (_record$category = record === null || record === void 0 ? void 0 : record.category) !== null && _record$category !== void 0 ? _record$category : item.category
            }), /*#__PURE__*/_jsx("span", {
              className: styles.headerSpacer
            }), confidence && /*#__PURE__*/_jsxs("span", {
              className: styles.confidence,
              children: [/*#__PURE__*/_jsx("span", {
                className: styles.confidenceLabel,
                children: "Confidence"
              }), /*#__PURE__*/_jsx("span", {
                className: styles.confidenceTrack,
                role: "progressbar",
                "aria-valuenow": confidence.pct,
                "aria-valuemin": 0,
                "aria-valuemax": 100,
                "aria-label": "RCA confidence",
                children: /*#__PURE__*/_jsx("span", {
                  className: styles.confidenceFill,
                  style: {
                    width: "".concat(confidence.pct, "%"),
                    backgroundColor: confidence.color
                  }
                })
              }), /*#__PURE__*/_jsxs("span", {
                className: styles.confidenceValue,
                style: {
                  color: confidence.color
                },
                children: [confidence.pct, "% \xB7 ", confidence.label]
              })]
            }), /*#__PURE__*/_jsx(CopyLinkButton, {
              className: styles.closeBtn
            }), /*#__PURE__*/_jsx("button", {
              type: "button",
              className: styles.closeBtn,
              "aria-label": "Close RCA",
              onClick: function onClick() {
                return closeIssueDrawer();
              },
              children: /*#__PURE__*/_jsx(XIcon, {
                size: 16
              })
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.body,
            children: [/*#__PURE__*/_jsxs("div", {
              className: styles.bugBanner,
              children: [/*#__PURE__*/_jsx("span", {
                className: styles.bugIcon,
                "aria-hidden": "true",
                children: /*#__PURE__*/_jsx(BugIcon, {
                  size: 16,
                  fill: "#cf222e"
                })
              }), /*#__PURE__*/_jsx("span", {
                className: styles.bugText,
                children: /*#__PURE__*/_jsx("span", {
                  className: styles.bugTitle,
                  children: title
                })
              })]
            }), /*#__PURE__*/_jsxs("div", {
              className: styles.sectionHead,
              children: [/*#__PURE__*/_jsx("span", {
                className: styles.sectionTitle,
                children: "Steps to Reproduce"
              }), steps.length > 0 && /*#__PURE__*/_jsxs("button", {
                type: "button",
                className: styles.viewAllLink,
                onClick: viewAllSteps,
                children: ["View all steps (", actionStepCount(steps), ")", /*#__PURE__*/_jsx(LinkExternalIcon, {
                  size: 16,
                  "aria-hidden": "true"
                })]
              })]
            }), loading && /*#__PURE__*/_jsx("span", {
              className: styles.loadingSpinner,
              "aria-label": "Loading steps",
              children: /*#__PURE__*/_jsx(LTLoader, {})
            }), !loading && steps.length === 0 && /*#__PURE__*/_jsx("span", {
              className: styles.loadingText,
              children: "This test's steps couldn't be read from the pack."
            }), /*#__PURE__*/_jsx("ul", {
              className: styles.timeline,
              children: timeline.map(function (entry, i) {
                var _drawerData$network, _drawerData$console;
                var first = i === 0;
                var last = i === timeline.length - 1;
                if (entry.kind === 'hidden') {
                  return /*#__PURE__*/_jsx(TimelineRow, {
                    first: first,
                    last: last,
                    icon: /*#__PURE__*/_jsx("span", {
                      className: styles.hiddenDot
                    }),
                    children: /*#__PURE__*/_jsxs("button", {
                      type: "button",
                      className: styles.hiddenLink,
                      onClick: function onClick() {
                        return setExpandedSegments(function (prev) {
                          var next = new Set(prev);
                          if (entry.expanded) next["delete"](entry.segment);else next.add(entry.segment);
                          return next;
                        });
                      },
                      children: [entry.expanded ? /*#__PURE__*/_jsx(FoldIcon, {}) : /*#__PURE__*/_jsx(UnfoldIcon, {}), entry.expanded ? "Hide ".concat(entry.count, " step").concat(entry.count === 1 ? '' : 's') : "Show ".concat(entry.count, " hidden step").concat(entry.count === 1 ? '' : 's')]
                    })
                  }, "hidden-".concat(entry.segment));
                }
                var step = entry.step,
                  role = entry.role;
                return /*#__PURE__*/_jsxs(TimelineRow, {
                  first: first,
                  last: last,
                  icon: stepIconFor(role, step),
                  children: [/*#__PURE__*/_jsxs("span", {
                    className: styles.stepTitleRow,
                    children: [/*#__PURE__*/_jsx("span", {
                      className: styles.stepTitle,
                      children: step.title
                    }), role === 'root' && /*#__PURE__*/_jsx("span", {
                      className: styles.rootChip,
                      children: "Root Cause"
                    }), role === 'effect' && /*#__PURE__*/_jsx("span", {
                      className: styles.effectChip,
                      children: "Effect"
                    }), role === 'root' && record && /*#__PURE__*/_jsxs(_Fragment, {
                      children: [/*#__PURE__*/_jsx("span", {
                        className: styles.stepSpacer
                      }), /*#__PURE__*/_jsx("button", {
                        type: "button",
                        className: styles.hideBtn,
                        onClick: function onClick() {
                          return setTabsHidden(function (v) {
                            return !v;
                          });
                        },
                        children: tabsHidden ? 'Show' : 'Hide'
                      })]
                    })]
                  }), role === 'root' && (record === null || record === void 0 ? void 0 : record.rootCause) && /*#__PURE__*/_jsx("span", {
                    className: styles.stepDesc,
                    children: record.rootCause
                  }), role !== 'root' && function (_step$intent) {
                    var desc = (_step$intent = step.intent) !== null && _step$intent !== void 0 ? _step$intent : step.reasoning;
                    // Skip descriptions that just repeat the title.
                    return desc && desc !== step.title ? /*#__PURE__*/_jsx("span", {
                      className: styles.stepDesc,
                      children: desc
                    }) : null;
                  }(), role === 'root' && record && !tabsHidden && /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx("div", {
                      className: styles.tabRow,
                      role: "tablist",
                      "aria-label": "Failure context",
                      children: [['console', 'Console'], ['network', 'Network'], ['media', 'Media']].map(function (_ref5) {
                        var _ref6 = _slicedToArray(_ref5, 2),
                          key = _ref6[0],
                          label = _ref6[1];
                        return /*#__PURE__*/_jsxs(React.Fragment, {
                          children: [/*#__PURE__*/_jsx("button", {
                            type: "button",
                            role: "tab",
                            "aria-selected": tab === key,
                            className: tab === key ? styles.tabSelected : styles.tab,
                            onClick: function onClick() {
                              return setTab(key);
                            },
                            children: label
                          }), key === 'network' && /*#__PURE__*/_jsx(LTTooltip, {
                            text: TRAJECTORY_SOON,
                            direction: "bottom",
                            children: /*#__PURE__*/_jsx("button", {
                              type: "button",
                              className: styles.tabDisabled,
                              disabled: true,
                              children: "Trajectory"
                            })
                          })]
                        }, key);
                      })
                    }), tab === 'network' && /*#__PURE__*/_jsx(LogBlock, {
                      title: "Network Logs",
                      emptyText: "No network requests captured at failure.",
                      note: drawerData.networkScope === 'all' ? 'step markers absent — showing all requests' : undefined,
                      lines: ((_drawerData$network = drawerData.network) !== null && _drawerData$network !== void 0 ? _drawerData$network : []).map(function (l) {
                        var _l$status;
                        return {
                          text: "".concat(l.method, " ").concat(l.url, " - [").concat((_l$status = l.status) !== null && _l$status !== void 0 ? _l$status : '—').concat(l.statusText ? " ".concat(l.statusText) : '', "]"),
                          failed: l.failed
                        };
                      })
                    }), tab === 'console' && /*#__PURE__*/_jsx(LogBlock, {
                      title: "Console Logs",
                      emptyText: "No console entries captured at failure.",
                      note: drawerData.consoleScope === 'all' ? 'step markers absent — showing all entries' : undefined,
                      lines: ((_drawerData$console = drawerData.console) !== null && _drawerData$console !== void 0 ? _drawerData$console : []).map(function (e) {
                        return {
                          text: "[".concat(e.level, "] ").concat(e.text),
                          failed: e.level === 'error'
                        };
                      })
                    }), tab === 'media' && /*#__PURE__*/_jsx(MediaTab, {
                      screenshotPath: drawerData.screenshotPath
                    })]
                  })]
                }, step.key);
              })
            }), fixText && /*#__PURE__*/_jsxs("div", {
              className: styles.fixSection,
              children: [/*#__PURE__*/_jsxs("div", {
                className: styles.sectionHead,
                children: [/*#__PURE__*/_jsx("span", {
                  className: styles.sectionTitle,
                  children: "How to fix it"
                }), /*#__PURE__*/_jsxs("button", {
                  type: "button",
                  className: styles.fixCopyBtn,
                  onClick: function onClick() {
                    return copyFix(fixPrompt);
                  },
                  children: [fixCopied ? /*#__PURE__*/_jsx(CheckIcon, {
                    size: 16
                  }) : /*#__PURE__*/_jsx(CopyIcon, {
                    size: 16
                  }), fixCopied ? 'Copied' : 'Copy fix prompt']
                })]
              }), /*#__PURE__*/_jsx("div", {
                className: styles.fixBox,
                children: fixText
              })]
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.footer,
            children: [/*#__PURE__*/_jsxs("span", {
              className: styles.feedback,
              children: [/*#__PURE__*/_jsx("span", {
                className: styles.feedbackLabel,
                children: "Was this helpful?"
              }), /*#__PURE__*/_jsx("button", {
                type: "button",
                className: vote === 'up' ? styles.voteActive : styles.voteBtn,
                "aria-label": "Helpful",
                "aria-pressed": vote === 'up',
                onClick: function onClick() {
                  return setVote(function (v) {
                    return v === 'up' ? null : 'up';
                  });
                },
                children: /*#__PURE__*/_jsx(ThumbsupIcon, {
                  size: 16
                })
              }), /*#__PURE__*/_jsx("button", {
                type: "button",
                className: vote === 'down' ? styles.voteActive : styles.voteBtn,
                "aria-label": "Not helpful",
                "aria-pressed": vote === 'down',
                onClick: function onClick() {
                  return setVote(function (v) {
                    return v === 'down' ? null : 'down';
                  });
                },
                children: /*#__PURE__*/_jsx(ThumbsdownIcon, {
                  size: 16
                })
              }), vote && /*#__PURE__*/_jsx("span", {
                className: styles.feedbackThanks,
                role: "status",
                children: "Thanks for the feedback!"
              })]
            }), /*#__PURE__*/_jsxs("button", {
              type: "button",
              className: styles.copyRcaBtn,
              onClick: function onClick() {
                return copyRca(rcaText);
              },
              children: [rcaCopied ? /*#__PURE__*/_jsx(CheckIcon, {
                size: 16
              }) : /*#__PURE__*/_jsx(CopyIcon, {
                size: 16
              }), rcaCopied ? 'Copied' : 'Copy RCA']
            })]
          })]
        })
      })
    })
  );
}